import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  Target, 
  FileText, 
  FolderOpen, 
  PlayCircle,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Settings,
  DollarSign,
  Search,
  Building2,
  BookOpen,
  Video,
  Contact,
  CheckSquare,
  Link2,
  CalendarDays,
  Bell,
  CheckCircle2,
  HelpCircle,
  Sparkles,
  Loader2,
  ClipboardList,
  ShieldCheck,
  Send,
  Wallet,
  ScrollText,
} from 'lucide-react';
import { Button } from './ui/button';
import ProductTour from './ProductTour';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import WelcomeModal from './WelcomeModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_enable-referral-hub/artifacts/1e30d90o_Enable%20Healthcare%20emblem_wordmark_BLUE.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_enable-referral-hub/artifacts/tywa2owq_Enable%20Healthcare%20emblem_BLUE.png";
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_enable-referral-hub/artifacts/1e30d90o_Enable%20Healthcare%20emblem_wordmark_BLUE.png";

const navItems = [
  { path: '/', label: 'Overview', icon: LayoutDashboard },
  { path: '/leads', label: 'Leads', icon: Target },
  { path: '/contacts', label: 'Contacts', icon: Contact },
  { path: '/activities', label: 'Tasks', icon: CheckSquare },
  { path: '/campaigns', label: 'Campaigns', icon: FileText },
  { path: '/commissions', label: 'Commissions', icon: DollarSign },
  { path: '/referrals', label: 'Referrals', icon: Link2 },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
];

const resourceItems = [
  { path: '/guidelines', label: 'Guidelines', icon: BookOpen },
  { path: '/media', label: 'Collateral', icon: FolderOpen },
  { path: '/videos', label: 'Video Library', icon: PlayCircle },
  { path: '/onboarding-docs', label: 'Onboarding Docs', icon: ClipboardList },
];

const adminItems = [
  { path: '/users', label: 'User Management', icon: Users },
  { path: '/templates', label: 'Doc Templates', icon: FileText },
];

const settingsItem = { path: '/settings', label: 'Settings', icon: Settings };

// Admin View sidebar navigation
const adminNavOverview = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
];
const adminNavAgents = [
  { path: '/admin/agents', label: 'Agents', icon: Users },
  { path: '/admin/invitations', label: 'Invitations', icon: Send },
  { path: '/admin/tasks', label: 'Tasks', icon: CheckSquare },
];
const adminNavLeads = [
  { path: '/leads', label: 'All Leads', icon: Target },
  { path: '/leads?view=pipeline', label: 'Pipeline', icon: Target },
  { path: '/leads?view=verification', label: 'Verification', icon: ShieldCheck },
];
const adminNavFinance = [
  { path: '/commissions', label: 'Commissions', icon: DollarSign },
  { path: '/admin/payouts', label: 'Payouts', icon: Wallet },
];
const adminNavAdmin = [
  { path: '/users', label: 'User Management', icon: Users },
  { path: '/templates', label: 'Doc Templates', icon: FileText },
  { path: '/admin/audit', label: 'Audit Log', icon: ScrollText },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const Layout = () => {
  const { user, logout, isAdmin, token, adminView, setAdminView } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const searchRef = useRef(null);
  const notifRef = useRef(null);

  // Auto-show tour for first-time users
  useEffect(() => {
    if (user && !localStorage.getItem('enable_tour_completed')) {
      const timer = setTimeout(() => setShowTour(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const [notifResp, countResp] = await Promise.all([
        axios.get(`${API_URL}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setNotifications(notifResp.data);
      setUnreadCount(countResp.data.count);
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    }
  }, [token]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Failed to mark notification read:', e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to mark all read:', e);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    setShowNotifications(false);
    if (notif.link) navigate(notif.link);
  };

  // Show welcome modal for agents on login — but NOT on first login (show tour instead)
  // Only show welcome modal if they've already completed the tour (2nd login onward)
  useEffect(() => {
    if (user && !isAdmin && localStorage.getItem('enable_tour_completed')) {
      const hasSeenWelcome = sessionStorage.getItem('hasSeenWelcomeModal');
      if (!hasSeenWelcome) {
        const timer = setTimeout(() => {
          setShowWelcomeModal(true);
          sessionStorage.setItem('hasSeenWelcomeModal', 'true');
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [user, isAdmin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const searchDebounceRef = useRef(null);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setAiSummary('');
    if (query.length < 2) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
      setShowSearchResults(true);
      
      // Debounced AI summary call
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(async () => {
        setAiLoading(true);
        try {
          const aiResp = await axios.get(`${API_URL}/api/search/ai?q=${encodeURIComponent(query)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (aiResp.data.ai_summary) {
            setAiSummary(aiResp.data.ai_summary);
          }
        } catch (e) {
          console.error('AI search error:', e);
        } finally {
          setAiLoading(false);
        }
      }, 600);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Auto-sync adminView state with admin routes
  useEffect(() => {
    if (isAdmin && location.pathname.startsWith('/admin/')) {
      setAdminView(true);
    }
  }, [location.pathname, isAdmin, setAdminView]);

  const handleSearchResultClick = (type, item) => {
    setShowSearchResults(false);
    setSearchQuery('');
    if (type === 'leads') {
      navigate(`/leads/${item.id}`);
    } else if (type === 'guidelines') {
      navigate('/guidelines');
    } else if (type === 'assets') {
      navigate('/media');
    } else if (type === 'videos') {
      navigate('/videos');
    }
  };

  const NavItem = ({ item, mobile = false }) => (
    <NavLink
      to={item.path}
      onClick={() => mobile && setMobileMenuOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
          isActive
            ? 'bg-white/20 text-white shadow-md'
            : 'text-white/80 hover:bg-white/10 hover:text-white'
        }`
      }
      data-testid={`nav-${item.path.replace('/', '') || 'overview'}`}
    >
      <item.icon className="w-5 h-5" />
      <span className={`${!sidebarOpen && !mobile ? 'hidden' : ''}`}>{item.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-white flex">
      {/* Desktop Sidebar - Blue Background */}
      <aside 
        className={`hidden md:flex flex-col bg-[#1B365D] transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            {sidebarOpen ? (
              <div className="flex items-center gap-2">
                <img 
                  src={LOGO_ICON} 
                  alt="Enable Healthcare" 
                  className="h-10 w-10 object-contain brightness-0 invert"
                  data-testid="logo"
                />
                <span className="text-white font-semibold text-sm">Enable Healthcare</span>
              </div>
            ) : (
              <img 
                src={LOGO_ICON} 
                alt="Enable Healthcare" 
                className="h-10 w-10 object-contain brightness-0 invert"
                data-testid="logo-icon"
              />
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="toggle-sidebar"
            >
              <ChevronRight className={`w-4 h-4 text-white transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {adminView ? (
              <>
                <div className={`px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider ${!sidebarOpen ? 'hidden' : ''}`}>Overview</div>
                {adminNavOverview.map((item) => <NavItem key={item.path} item={item} />)}
                <div className="my-4 mx-4 border-t border-white/20" />
                <div className={`px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider ${!sidebarOpen ? 'hidden' : ''}`}>Agents</div>
                {adminNavAgents.map((item) => <NavItem key={item.path + item.label} item={item} />)}
                <div className="my-4 mx-4 border-t border-white/20" />
                <div className={`px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider ${!sidebarOpen ? 'hidden' : ''}`}>Leads</div>
                {adminNavLeads.map((item) => <NavItem key={item.path + item.label} item={item} />)}
                <div className="my-4 mx-4 border-t border-white/20" />
                <div className={`px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider ${!sidebarOpen ? 'hidden' : ''}`}>Finance</div>
                {adminNavFinance.map((item) => <NavItem key={item.path + item.label} item={item} />)}
                <div className="my-4 mx-4 border-t border-white/20" />
                <div className={`px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider ${!sidebarOpen ? 'hidden' : ''}`}>Admin</div>
                {adminNavAdmin.map((item) => <NavItem key={item.path + item.label} item={item} />)}
              </>
            ) : (
              <>
                {navItems.map((item) => <NavItem key={item.path} item={item} />)}
                <div className="my-4 mx-4 border-t border-white/20" />
                <div className={`px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider ${!sidebarOpen ? 'hidden' : ''}`}>Resources</div>
                {resourceItems.map((item) => <NavItem key={item.path} item={item} />)}
                {isAdmin && (
                  <>
                    <div className="my-4 mx-4 border-t border-white/20" />
                    <div className={`px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider ${!sidebarOpen ? 'hidden' : ''}`}>Admin</div>
                    {adminItems.map((item) => <NavItem key={item.path} item={item} />)}
                  </>
                )}
                <div className="my-4 mx-4 border-t border-white/20" />
                <NavItem item={settingsItem} />
              </>
            )}
          </nav>
        </ScrollArea>

        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className={`flex items-center gap-3 ${!sidebarOpen ? 'justify-center' : ''}`}>
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-white/60 capitalize">{user?.role}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#1B365D] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img 
            src={LOGO_ICON} 
            alt="Enable Healthcare" 
            className="h-8 object-contain brightness-0 invert"
          />
          <span className="text-white font-semibold text-sm">Enable Healthcare</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-white/10 text-white"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="md:hidden fixed inset-0 z-40 bg-[#1B365D] pt-16"
          >
            <ScrollArea className="h-full">
              <nav className="p-4 space-y-2">
                {adminView ? (
                  <>
                    <div className="px-4 py-2 text-xs font-semibold text-white/50 uppercase">Overview</div>
                    {adminNavOverview.map((item) => <NavItem key={item.path} item={item} mobile />)}
                    <div className="my-4 border-t border-white/20" />
                    <div className="px-4 py-2 text-xs font-semibold text-white/50 uppercase">Agents</div>
                    {adminNavAgents.map((item) => <NavItem key={item.path + item.label} item={item} mobile />)}
                    <div className="my-4 border-t border-white/20" />
                    <div className="px-4 py-2 text-xs font-semibold text-white/50 uppercase">Leads</div>
                    {adminNavLeads.map((item) => <NavItem key={item.path + item.label} item={item} mobile />)}
                    <div className="my-4 border-t border-white/20" />
                    <div className="px-4 py-2 text-xs font-semibold text-white/50 uppercase">Finance</div>
                    {adminNavFinance.map((item) => <NavItem key={item.path + item.label} item={item} mobile />)}
                    <div className="my-4 border-t border-white/20" />
                    <div className="px-4 py-2 text-xs font-semibold text-white/50 uppercase">Admin</div>
                    {adminNavAdmin.map((item) => <NavItem key={item.path + item.label} item={item} mobile />)}
                  </>
                ) : (
                  <>
                    {navItems.map((item) => <NavItem key={item.path} item={item} mobile />)}
                    {isAdmin && (
                      <>
                        <div className="my-4 border-t border-white/20" />
                        <div className="px-4 py-2 text-xs font-semibold text-white/50 uppercase">Admin</div>
                        {adminItems.map((item) => <NavItem key={item.path} item={item} mobile />)}
                      </>
                    )}
                  </>
                )}
                
                <div className="mt-6 p-4 bg-white/10 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-white">{user?.name}</p>
                      <p className="text-sm text-white/60 capitalize">{user?.role}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      onClick={() => { setMobileMenuOpen(false); navigate('/settings'); }}
                      className="w-full text-white border-white/30 hover:bg-white/10"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleLogout}
                      className="w-full text-white border-white/30 hover:bg-white/10"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                </div>
              </nav>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 min-h-screen md:pt-0 pt-16 flex flex-col">
        {/* Search Header */}
        <div className="hidden md:flex items-center justify-between px-8 py-4 border-b border-slate-100 bg-white">
          <div className="relative w-96" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="AI Search — leads, guidelines, media..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchResults && setShowSearchResults(true)}
              className="pl-10 rounded-full border-slate-200 bg-slate-50 focus:bg-white"
              data-testid="universal-search"
            />
            
            {/* Search Results Dropdown */}
            <AnimatePresence>
              {showSearchResults && searchResults && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50"
                  style={{ width: '480px' }}
                >
                  <div className="max-h-[480px] overflow-y-auto">
                    {/* AI Summary */}
                    {(aiLoading || aiSummary) && (
                      <div className="p-3 bg-gradient-to-r from-[#1B365D]/5 to-[#0071E3]/5 border-b border-slate-100" data-testid="ai-search-summary">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-[#0071E3] mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#0071E3] mb-1">AI Summary</p>
                            {aiLoading ? (
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Analyzing results...
                              </div>
                            ) : (
                              <p className="text-xs text-slate-700 leading-relaxed">{aiSummary}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Leads Results */}
                    {searchResults.leads?.length > 0 && (
                      <div className="p-2">
                        <p className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase">Leads</p>
                        {searchResults.leads.map(lead => (
                          <button
                            key={lead.id}
                            onClick={() => handleSearchResultClick('leads', lead)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                          >
                            <div className="w-8 h-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-[#1B365D]" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{lead.organization_name}</p>
                              <p className="text-xs text-slate-500">{lead.contact_name}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Guidelines Results */}
                    {searchResults.guidelines?.length > 0 && (
                      <div className="p-2 border-t border-slate-100">
                        <p className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase">Guidelines</p>
                        {searchResults.guidelines.map(guideline => (
                          <button
                            key={guideline.id}
                            onClick={() => handleSearchResultClick('guidelines', guideline)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                          >
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                              <BookOpen className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{guideline.title}</p>
                              <p className="text-xs text-slate-500">{guideline.category}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Assets Results */}
                    {searchResults.assets?.length > 0 && (
                      <div className="p-2 border-t border-slate-100">
                        <p className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase">Media</p>
                        {searchResults.assets.map(asset => (
                          <button
                            key={asset.id}
                            onClick={() => handleSearchResultClick('assets', asset)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                          >
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <FolderOpen className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{asset.name}</p>
                              <p className="text-xs text-slate-500">{asset.category}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Videos Results */}
                    {searchResults.videos?.length > 0 && (
                      <div className="p-2 border-t border-slate-100">
                        <p className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase">Videos</p>
                        {searchResults.videos.map(video => (
                          <button
                            key={video.id}
                            onClick={() => handleSearchResultClick('videos', video)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                          >
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <Video className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{video.title}</p>
                              <p className="text-xs text-slate-500">{video.category}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* No Results */}
                    {(!searchResults.leads?.length && !searchResults.guidelines?.length && 
                      !searchResults.assets?.length && !searchResults.videos?.length) && (
                      <div className="p-8 text-center text-slate-500">
                        No results found for "{searchQuery}"
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Admin/Agent View Switcher - only visible to admins */}
            {isAdmin && (
              <div className="flex bg-[#f0f0f0] rounded-lg p-[3px] mr-2" data-testid="view-switcher">
                <button
                  onClick={() => { setAdminView(false); navigate('/'); }}
                  className={`text-xs py-[5px] px-[14px] rounded-md transition-all ${
                    !adminView ? 'bg-white text-[#111] font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.1)]' : 'text-[#888] bg-transparent'
                  }`}
                  data-testid="view-switch-my"
                >
                  My View
                </button>
                <button
                  onClick={() => { setAdminView(true); navigate('/admin/dashboard'); }}
                  className={`text-xs py-[5px] px-[14px] rounded-md transition-all ${
                    adminView ? 'bg-white text-[#111] font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.1)]' : 'text-[#888] bg-transparent'
                  }`}
                  data-testid="view-switch-admin"
                >
                  Admin View
                </button>
              </div>
            )}

            {/* Tour Button */}
            <button
              onClick={() => { localStorage.removeItem('enable_tour_completed'); setShowTour(true); }}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              title="Product Tour"
              data-testid="header-tour-btn"
            >
              <HelpCircle className="w-5 h-5 text-slate-600" />
            </button>
            
            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="notification-bell"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 top-12 w-96 bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden"
                    data-testid="notification-dropdown"
                  >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-900 text-sm">Notifications</h3>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-[#0071E3] hover:underline" data-testid="mark-all-read">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.slice(0, 20).map(notif => (
                          <button
                            key={notif.id}
                            onClick={() => handleNotificationClick(notif)}
                            className={`w-full text-left p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${!notif.read ? 'bg-blue-50/50' : ''}`}
                            data-testid={`notif-${notif.id}`}
                          >
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!notif.read ? 'bg-[#0071E3]' : 'bg-transparent'}`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{notif.title}</p>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                              <p className="text-[10px] text-slate-400 mt-1">
                                {new Date(notif.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </p>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="p-8 text-center text-slate-400">
                          <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 hover:bg-slate-50 rounded-full p-1 pr-3 transition-colors" data-testid="user-menu-trigger">
                  <div className="w-10 h-10 rounded-full bg-[#1B365D] flex items-center justify-center text-white font-semibold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 rotate-90" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { localStorage.removeItem('enable_tour_completed'); setShowTour(true); }} className="cursor-pointer" data-testid="start-tour-btn">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Start Tour
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="p-6 md:p-8">
            <Outlet />
          </div>
        </ScrollArea>
      </main>

      {/* Welcome Modal for Agents */}
      {showWelcomeModal && (
        <WelcomeModal onClose={() => setShowWelcomeModal(false)} />
      )}

      {/* Product Tour */}
      <ProductTour show={showTour} onClose={() => setShowTour(false)} />
    </div>
  );
};

export default Layout;
